import React, { useState, useEffect, useRef } from 'react';
import { Horse } from './types';
import { loadHorsesData } from './horsesData';
import { saveHorseList } from './api';
import './WeightEditor.css';

const WeightEditor: React.FC = () => {
  const [horses, setHorses] = useState<Horse[]>([]);
  const [allHorses, setAllHorses] = useState<Horse[]>([]); // Keep all horses for saving
  const [nextId, setNextId] = useState(43);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const inputRef = useRef<HTMLInputElement>(null);

  // Determine if a horse should be visible based on hidden status and contract end date
  const isHorseVisible = (horse: Horse): boolean => {
    // If not hidden, always visible
    if (!horse.hidden) return true;

    // If hidden, check if contract end date is in current month or future
    if (horse.contract?.end_date) {
      const endDate = new Date(horse.contract.end_date);
      const today = new Date();

      // Get year-month for comparison (year * 12 + month gives comparable values)
      const endYearMonth = endDate.getFullYear() * 12 + endDate.getMonth();
      const currentYearMonth = today.getFullYear() * 12 + today.getMonth();

      // Visible if contract ends in current month or later
      return endYearMonth >= currentYearMonth;
    }

    // If hidden and no end date, hide it
    return false;
  };

  // Load and filter horses
  useEffect(() => {
    loadHorsesData()
      .then(({ horses, nextId }) => {
        setAllHorses(horses); // Store all horses
        const visibleHorses = horses
          .filter(h => isHorseVisible(h))
          .sort((a, b) => a.name.localeCompare(b.name));
        setHorses(visibleHorses);
        setNextId(nextId);
      })
      .catch((error) => {
        console.error('Failed to load horses:', error);
        alert('Fehler beim Laden der Pferde-Daten');
      });
  }, []);

  // Auto-focus input
  useEffect(() => {
    inputRef.current?.focus();
  }, [currentIndex]);

  const saveToServer = async (updatedHorses: Horse[]) => {
    setSaveStatus('saving');
    try {
      // Create full list including hidden horses
      const fullHorseList = allHorses.map(h => {
        const updated = updatedHorses.find(uh => uh.id === h.id);
        return updated || h;
      });

      const data = {
        ponyNextID: nextId,
        ponys: fullHorseList.reduce((acc, horse) => {
          acc[horse.id] = horse;
          return acc;
        }, {} as Record<string, Horse>)
      };

      await saveHorseList(data);

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 1000);
    } catch (error: any) {
      console.error('Error saving:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    // Max 2 digits
    if (value.length > 2) return;

    setInputValue(value);

    // When 2 digits entered, process it
    if (value.length === 2) {
      const firstDigit = parseInt(value[0]);
      const secondDigit = parseInt(value[1]);

      // Validate: first digit 1-9, second digit 0 or 5
      if (firstDigit >= 1 && firstDigit <= 9 && (secondDigit === 0 || secondDigit === 5)) {
        const weight = firstDigit * 100 + secondDigit * 10;

        // Update horse weight
        const updatedHorses = horses.map((h, idx) =>
          idx === currentIndex ? { ...h, weight } : h
        );
        setHorses(updatedHorses);

        // Save to server
        saveToServer(updatedHorses);

        // Clear input
        setInputValue('');

        // Move to next horse
        if (currentIndex < horses.length - 1) {
          setCurrentIndex(currentIndex + 1);
        }
      } else {
        // Invalid input, clear and stay
        setInputValue('');
        alert('Ungültige Eingabe! Erste Ziffer: 1-9, Zweite Ziffer: 0 oder 5');
      }
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setInputValue('');
    }
  };

  const handleNext = () => {
    if (currentIndex < horses.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setInputValue('');
    }
  };

  if (horses.length === 0) {
    return (
      <div className="weight-editor">
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Lade Pferde...</p>
        </div>
      </div>
    );
  }

  const currentHorse = horses[currentIndex];

  return (
    <div className="weight-editor">
      <header className="header">
        <h2>Gewichts-Editor (Schnelleingabe)</h2>
        <h4>
          Pferd <strong>{currentIndex + 1}</strong> von <strong>{horses.length}</strong>
          {saveStatus !== 'idle' && (
            <span className={`save-indicator save-${saveStatus}`}>
              {saveStatus === 'saving' && ' • Speichert...'}
              {saveStatus === 'saved' && ' • ✓'}
              {saveStatus === 'error' && ' • Fehler'}
            </span>
          )}
        </h4>
      </header>

      <div className="editor-main">
        <div className="current-horse">
          <h3>{currentHorse.name}</h3>
          <p className="owner">{currentHorse.owner}</p>
          <p className="current-weight">
            Aktuelles Gewicht: <strong>{currentHorse.weight} kg</strong>
          </p>
        </div>

        <div className="input-section">
          <label>Neue Gewichtseingabe (2 Ziffern):</label>
          <div className="input-group">
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              value={inputValue}
              onChange={handleInputChange}
              maxLength={2}
            />
            <span className="input-hint">
              {inputValue.length === 1 && `${inputValue}_ → ${parseInt(inputValue) * 100 + 0}kg oder ${parseInt(inputValue) * 100 + 50}kg`}
              {inputValue.length === 2 && `→ ${parseInt(inputValue[0]) * 100 + parseInt(inputValue[1]) * 10}kg`}
            </span>
          </div>
          <p className="help-text">
            Erste Ziffer: 1-9 | Zweite Ziffer: 0 oder 5 | Ergebnis: 100-950 kg
          </p>
        </div>

        <div className="navigation-buttons">
          <button
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            className="btn-prev"
          >
            ← Vorheriges
          </button>
          <button
            onClick={handleNext}
            disabled={currentIndex === horses.length - 1}
            className="btn-next"
          >
            Nächstes →
          </button>
        </div>
      </div>

      <div className="horse-list">
        <h4>Alle Pferde:</h4>
        <div className="horse-list-scroll">
          {horses.map((horse, idx) => (
            <div
              key={horse.id}
              className={`horse-item ${idx === currentIndex ? 'active' : ''} ${horse.weight > 0 ? 'has-weight' : ''}`}
              onClick={() => {
                setCurrentIndex(idx);
                setInputValue('');
              }}
            >
              <span className="horse-name">{horse.name}</span>
              <span className="horse-weight">{horse.weight} kg</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WeightEditor;
